import { useCallback, useEffect, useState } from 'react'
import {
  Cloud,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  CloudRain,
  CloudSnow,
  LocateFixed,
  MapPin,
  Sun,
  Thermometer,
} from 'lucide-react'

import { cn } from '@/lib/utils'

/** @typedef {'idle' | 'prompt' | 'loading' | 'ready' | 'denied' | 'error'} WeatherStatus */
/** @typedef {'dashboard' | 'compact'} WeatherVariant */

/**
 * @param {number} code
 * @returns {{ label: string, Icon: typeof Sun }}
 */
function weatherFromCode(code) {
  if (code === 0) return { label: 'Clear', Icon: Sun }
  if (code <= 3) return { label: 'Cloudy', Icon: Cloud }
  if (code <= 48) return { label: 'Foggy', Icon: CloudFog }
  if (code <= 57) return { label: 'Drizzle', Icon: CloudDrizzle }
  if (code <= 67) return { label: 'Rain', Icon: CloudRain }
  if (code <= 77) return { label: 'Snow', Icon: CloudSnow }
  if (code <= 82) return { label: 'Showers', Icon: CloudRain }
  if (code <= 86) return { label: 'Snow', Icon: CloudSnow }
  return { label: 'Storm', Icon: CloudLightning }
}

function formatClock(date) {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date)
}

/**
 * Live clock + browser geolocation + Open-Meteo weather.
 *
 * @param {{ variant?: WeatherVariant, className?: string }} [props]
 */
export function LocalWeatherWidget({ variant = 'dashboard', className }) {
  const [now, setNow] = useState(() => new Date())
  /** @type {[WeatherStatus, (s: WeatherStatus) => void]} */
  const [status, setStatus] = useState('idle')
  const [place, setPlace] = useState(/** @type {string | null} */ (null))
  const [tempC, setTempC] = useState(/** @type {number | null} */ (null))
  const [weatherCode, setWeatherCode] = useState(/** @type {number | null} */ (null))
  const [errorMessage, setErrorMessage] = useState(/** @type {string | null} */ (null))

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(id)
  }, [])

  const loadWeather = useCallback(async (latitude, longitude) => {
    setStatus('loading')
    setErrorMessage(null)

    try {
      const [weatherRes, geoRes] = await Promise.all([
        fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,weather_code&timezone=auto`,
        ),
        fetch(
          `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`,
        ),
      ])

      if (!weatherRes.ok) throw new Error('Weather unavailable')
      const weatherJson = await weatherRes.json()
      const temp = weatherJson?.current?.temperature_2m
      const code = weatherJson?.current?.weather_code
      if (typeof temp !== 'number' || typeof code !== 'number') {
        throw new Error('Weather unavailable')
      }

      setTempC(Math.round(temp))
      setWeatherCode(code)

      if (geoRes.ok) {
        const geoJson = await geoRes.json()
        const city =
          geoJson.city ||
          geoJson.locality ||
          geoJson.principalSubdivision ||
          geoJson.countryName ||
          'Your area'
        setPlace(city)
      } else {
        setPlace(`${latitude.toFixed(2)}°, ${longitude.toFixed(2)}°`)
      }

      setStatus('ready')
    } catch (err) {
      setStatus('error')
      setErrorMessage(err instanceof Error ? err.message : 'Could not load weather')
    }
  }, [])

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setStatus('error')
      setErrorMessage('Location not supported')
      return
    }

    setStatus('prompt')
    setErrorMessage(null)

    navigator.geolocation.getCurrentPosition(
      (position) => {
        void loadWeather(position.coords.latitude, position.coords.longitude)
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setStatus('denied')
          setErrorMessage('Location blocked')
        } else {
          setStatus('error')
          setErrorMessage('Could not get location')
        }
      },
      { enableHighAccuracy: false, timeout: 12_000, maximumAge: 10 * 60_000 },
    )
  }, [loadWeather])

  useEffect(() => {
    if (!navigator.permissions?.query) return undefined
    let cancelled = false

    navigator.permissions
      .query({ name: /** @type {PermissionName} */ ('geolocation') })
      .then((result) => {
        if (cancelled) return
        if (result.state === 'granted') requestLocation()
      })
      .catch(() => {
        /* Permissions API unavailable — wait for click */
      })

    return () => {
      cancelled = true
    }
  }, [requestLocation])

  const { label: weatherLabel, Icon: WeatherIcon } =
    weatherCode == null ? { label: 'Weather', Icon: Thermometer } : weatherFromCode(weatherCode)

  const isBusy = status === 'prompt' || status === 'loading'
  const needsAction = status === 'idle' || status === 'denied' || status === 'error'
  const isDashboard = variant === 'dashboard'

  if (isDashboard) {
    return (
      <div
        className={cn(
          'inline-flex h-10 min-w-[18rem] max-w-full shrink-0 items-center gap-3 overflow-hidden rounded-2xl',
          'border border-[#E5DED3]/90 bg-white/85 px-3 shadow-sm backdrop-blur-md',
          'sm:min-w-[20rem] sm:gap-3.5 sm:px-3.5',
          className,
        )}
        aria-label="Local time and weather"
      >
        <div className="flex min-w-0 items-center gap-2 border-r border-[#E5DED3] pe-3">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-primary/80">Local</p>
          <p className="truncate text-[13px] font-semibold tabular-nums leading-none text-foreground">
            {formatClock(now)}
          </p>
        </div>

        {status === 'ready' ? (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#C9A66B] to-[#8A6D42] text-white shadow-sm shadow-[#8A6D42]/20">
              <WeatherIcon className="h-3.5 w-3.5" strokeWidth={2.25} aria-hidden />
            </span>
            <p className="flex min-w-0 items-center gap-1.5 truncate text-[13px] font-semibold text-foreground">
              <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
              <span className="truncate">{place}</span>
              <span className="shrink-0 font-medium text-muted-foreground">
                · {tempC}&deg;C · {weatherLabel}
              </span>
            </p>
          </div>
        ) : (
          <button
            type="button"
            onClick={requestLocation}
            disabled={isBusy}
            className="flex min-w-0 flex-1 items-center gap-2 text-left transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-80"
            title={errorMessage ?? 'Allow location to show weather'}
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#C9A66B] to-[#8A6D42] text-white shadow-sm">
              <LocateFixed
                className={cn('h-3.5 w-3.5', isBusy && 'animate-pulse')}
                strokeWidth={2.25}
                aria-hidden
              />
            </span>
            <span className="min-w-0 truncate text-[13px] font-semibold text-foreground">
              {isBusy
                ? 'Getting location…'
                : needsAction && status !== 'idle'
                  ? 'Retry location'
                  : 'Enable location'}
            </span>
          </button>
        )}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex min-w-0 shrink-0 items-stretch overflow-hidden rounded-2xl',
        'border border-white/20 bg-white/10 shadow-sm backdrop-blur-sm',
        className,
      )}
      aria-label="Local time and weather"
    >
      <div className="flex min-w-0 flex-col justify-center border-r border-white/15 px-2.5 py-1.5">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-white/55">Local</p>
        <p className="truncate text-[12px] font-semibold tabular-nums leading-tight text-white">
          {formatClock(now)}
        </p>
      </div>

      {status === 'ready' ? (
        <div className="flex min-w-0 items-center gap-2.5 px-2.5 py-1.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 text-[#E2D2B5] ring-1 ring-white/20">
            <WeatherIcon className="h-4 w-4" strokeWidth={2.25} aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="flex items-center gap-1 truncate text-[12px] font-semibold leading-tight text-white">
              <MapPin className="h-3 w-3 shrink-0 text-[#C9A66B]" aria-hidden />
              <span className="truncate">{place}</span>
            </p>
            <p className="truncate text-[11px] text-white/75">
              {tempC}&deg;C · {weatherLabel}
            </p>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={requestLocation}
          disabled={isBusy}
          className="flex min-w-0 items-center gap-2.5 px-2.5 py-1.5 text-left transition-colors hover:bg-white/10 disabled:cursor-wait disabled:opacity-80"
          title={errorMessage ?? 'Allow location to show weather'}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/15 text-[#E2D2B5] ring-1 ring-white/20">
            <LocateFixed
              className={cn('h-4 w-4', isBusy && 'animate-pulse')}
              strokeWidth={2.25}
              aria-hidden
            />
          </div>
          <div className="min-w-0">
            <p className="truncate text-[12px] font-semibold leading-tight text-white">
              {isBusy ? 'Getting location…' : needsAction && status !== 'idle' ? 'Retry location' : 'Enable location'}
            </p>
            <p className="truncate text-[11px] text-white/70">
              {errorMessage ?? 'Weather & place near you'}
            </p>
          </div>
        </button>
      )}
    </div>
  )
}
